// One audio and one video transceiver per peer; screen changes need no renegotiation.
export default class VoiceSession {
  constructor(send, onStreams, onError) {
    this.send = send; this.onStreams = onStreams; this.onError = onError
    this.peers = new Map(); this.pending = new Map(); this.streams = {}
    this.iceServers = []; this.local = null; this.screen = null
  }
  async start(config) {
    if (!navigator.mediaDevices) throw new Error('Ses ve ekran paylaşımı için HTTPS veya localhost gerekli.')
    this.iceServers = config.iceServers
    this.noiseSuppression = true
    this.local = await this.captureAudio()
  }
  async captureAudio() {
    return navigator.mediaDevices.getUserMedia({
      audio: {
        noiseSuppression: this.noiseSuppression,
        echoCancellation: true,
        autoGainControl: true,
        channelCount: 1
      },
      video: false
    })
  }
  peer(id) {
    if (this.peers.has(id)) return this.peers.get(id)
    const pc = new RTCPeerConnection({ iceServers: this.iceServers })
    pc.addTransceiver(this.local.getAudioTracks()[0], { direction: 'sendrecv', streams: [this.local] })
    const video = pc.addTransceiver('video', { direction: 'sendrecv' })
    if (this.screen) video.sender.replaceTrack(this.screen.getVideoTracks()[0]).catch(this.onError)
    pc.onicecandidate = ({ candidate }) => {
      if (candidate) this.send({ type: 'voice_ice_candidate', target_user_id: id, candidate })
    }
    pc.ontrack = ({ track }) => {
      if (!this.streams[id]) this.streams[id] = new MediaStream()
      this.streams[id].addTrack(track)
      this.onStreams({ ...this.streams })
    }
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed') this.onError(new Error('Medya bağlantısı kurulamadı. Farklı ağlarda TURN sunucusu gerekebilir.'))
    }
    this.peers.set(id, pc)
    return pc
  }
  async handle(event) {
    const id = event.from_user_id
    if (event.type === 'voice_ready') {
      for (const peerId of event.participants) {
        const pc = this.peer(peerId)
        await pc.setLocalDescription(await pc.createOffer())
        this.send({ type: 'voice_offer', target_user_id: peerId, sdp: pc.localDescription })
      }
    } else if (event.type === 'voice_offer') {
      const pc = this.peer(id)
      await pc.setRemoteDescription(event.sdp)
      await this.flush(id, pc)
      await pc.setLocalDescription(await pc.createAnswer())
      this.send({ type: 'voice_answer', target_user_id: id, sdp: pc.localDescription })
    } else if (event.type === 'voice_answer') {
      const pc = this.peers.get(id)
      if (pc) { await pc.setRemoteDescription(event.sdp); await this.flush(id, pc) }
    } else if (event.type === 'voice_ice_candidate') {
      const pc = this.peers.get(id)
      if (pc && pc.remoteDescription) await pc.addIceCandidate(event.candidate)
      else this.pending.set(id, [...(this.pending.get(id) || []), event.candidate])
    } else if (event.type === 'voice_peer_left') this.remove(event.user_id)
  }
  async flush(id, pc) {
    for (const candidate of this.pending.get(id) || []) await pc.addIceCandidate(candidate)
    this.pending.delete(id)
  }
  async share(height, fps, onEnded) {
    if (!navigator.mediaDevices.getDisplayMedia) throw new Error('Bu tarayıcı ekran paylaşımını desteklemiyor.')
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: { width: { ideal: Math.round(height * 16 / 9), max: 7680 }, height: { ideal: height, max: 4320 }, frameRate: { ideal: fps, max: fps } }, audio: false
    })
    const track = stream.getVideoTracks()[0]
    track.contentHint = 'detail'
    try {
      for (const pc of this.peers.values()) {
        await pc.getTransceivers().find(t => t.receiver.track.kind === 'video').sender.replaceTrack(track)
      }
    } catch (error) { stream.getTracks().forEach(t => t.stop()); throw error }
    if (this.screen) this.screen.getTracks().forEach(t => t.stop())
    this.screen = stream
    track.onended = onEnded
    return { stream, settings: track.getSettings() }
  }
  async stopShare() {
    if (this.screen) this.screen.getTracks().forEach(t => t.stop())
    this.screen = null
    await Promise.all([...this.peers.values()].map(pc => pc.getTransceivers().find(t => t.receiver.track.kind === 'video').sender.replaceTrack(null)))
  }
  mute(value) { if (this.local) this.local.getAudioTracks().forEach(t => { t.enabled = !value }) }
  async setNoiseSuppression(value) {
    if (!this.local) return false
    const oldTrack = this.local.getAudioTracks()[0]
    this.noiseSuppression = value
    const next = await this.captureAudio()
    const nextTrack = next.getAudioTracks()[0]
    for (const pc of this.peers.values()) {
      const sender = pc.getSenders().find(item => item.track && item.track.kind === 'audio')
      if (sender) await sender.replaceTrack(nextTrack)
    }
    nextTrack.enabled = oldTrack.enabled
    oldTrack.stop()
    this.local = next
    return true
  }
  remove(id) {
    const pc = this.peers.get(id)
    if (pc) pc.close()
    this.peers.delete(id); this.pending.delete(id); delete this.streams[id]
    this.onStreams({ ...this.streams })
  }
  close() {
    if (this.local) this.local.getTracks().forEach(t => t.stop())
    if (this.screen) this.screen.getTracks().forEach(t => t.stop())
    for (const id of this.peers.keys()) this.remove(id)
    this.pending.clear(); this.local = null; this.screen = null
  }
}
